/* eslint-disable prefer-const */
import React, {
  useCallback,
  useRef,
  useState,
  useEffect,
  useMemo,
} from "react";

import { styled } from "@mui/material/styles";
import {
  useNavigate,
  Outlet,
  useParams,
  Params,
  useLocation,
} from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";

import clsx from "clsx";
import color from "color";
import { motion } from "framer-motion";

import times from "lodash/times";
import reduce from "lodash/reduce";

import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import useSetState from "react-use/lib/useSetState";
import useClickAway from "react-use/lib/useClickAway";

import {
  useClientPlan,
  useLayout,
  useHidScannerSettings,
  useSocketScannerSettings,
} from "../../lib/hooks/useQueryBoard";

// import useEventTracking from "../../bit/tracking/useEventTracking";
// import usePageTracking from "../../bit/tracking/usePageTracking";

import Title from "../../components/Title";
import BackButton from "../../components/BackButton";
import VirtualKeyboard, {
  VirtualKeyboardRef,
} from "../../components/VirtualKeyboard";
import Curve from "../../components/Animations/PageTransitions/Curve";
import OfflineModal from "../../components/Offline/OfflineModal";
import AggrementDialog from "../../components/Aggrement/AggrementDialog";
import { useAgreementState } from "../../lib/hooks/useAgreementState";
import { useAcceptedAgreements } from "../../context/Agreement";

import { isVirtualKeyboardEnabled, isInWebView } from "../../config/const";
import { useOfflineContext } from "../../context/Offline";
import Reveal from "../../components/Animations/Reveal";
import Bump from "../../components/Animations/Bump";
import MoveUp from "../../components/Animations/MoveUp";

const Divider = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.text.primary,
}));

const ScannerButton = styled(Button)(() => ({
  fontSize: "2.5rem",
}));

export const PinInput = styled(motion.input)(({ theme }) => ({
  color: theme.palette.text.primary,
  borderColor: theme.palette.primary.main,
  background: color(theme.palette.background.default).lighten(0.1).hex(),

  "&:focus": {
    borderColor: theme.palette.primary.main,
    backgroundColor: color(theme.palette.background.default).darken(0.07).hex(),
  },

  "&.error": {
    borderColor: theme.palette.error.main,
  },
}));

interface IQueryParams extends Params {
  action: "checkin" | "checkout";
  code: string;
}

const defaultPinCode = {
  1: "",
  2: "",
  3: "",
  4: "",
  5: "",
  6: "",
};

const isMediaDevicesSupported = () => {
  const isMediaDevicesSupported =
    typeof navigator !== "undefined" && !!navigator.mediaDevices;

  return isMediaDevicesSupported;
};

const CheckPage = () => {
  const { t } = useTranslation(["check"]);

  const { action, code } = useParams() as IQueryParams;

  const { pathname } = useLocation();

  const mainRoute = new RegExp(`^\/access\/${action}\/?$`).test(pathname);

  const clientPlan = useClientPlan();
  const { online } = useOfflineContext();

  const isCameraFeatureEnabled = useMemo(() => {
    const isNotFree = clientPlan && clientPlan !== "free";

    if (!isNotFree) {
      return false;
    }

    return isMediaDevicesSupported();
  }, [clientPlan]);

  const { integratedManualCheckin, checkout, manualCheckout } = useLayout();

  const { isHidScannerActive } = useHidScannerSettings();
  const { isSocketScannerActive } = useSocketScannerSettings();

  const isExternalScannerActive = isHidScannerActive || isSocketScannerActive;

  const [pinCode, setPinCode] = useSetState(defaultPinCode);

  const [focusedInput, setFocusedInput] = useState<undefined | string>("1");

  const keyboard = useRef<VirtualKeyboardRef>(null);
  const keyboardBox = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  const { showFirst, universalAgreements } = useAgreementState();
  const { accept } = useAcceptedAgreements();

  /**
   * Nobody has identified themselves yet on this page, so only the documents
   * meant for every member type can go up here. The rest wait for the code.
   *
   * Derived rather than held in state: the board config can still be loading
   * when this mounts, and a list captured then would stay empty for good.
   */
  const pendingAgreements =
    action === "checkin" && showFirst && mainRoute ? universalAgreements : [];

  // usePageTracking({
  //   page: action,
  // });

  // const { trackEvent } = useEventTracking();

  const onKeyboardChange = useCallback(
    (value: string) => {
      const regex = /^\d$/;

      if (
        focusedInput &&
        +focusedInput < 7 &&
        (value === "" || regex.test(value))
      ) {
        /**
         * Reset virtual input after we set the code
         */
        if (keyboard.current) {
          keyboard.current.setInput("", focusedInput);
        }

        setPinCode({ [focusedInput]: value });

        if (focusedInput === "6" && value.length) {
          setFocusedInput(undefined);
        } else {
          if (document.activeElement) {
            (document.activeElement as HTMLInputElement)?.blur();
          }

          setFocusedInput(`${+focusedInput + 1}`);
        }
      }
    },
    [focusedInput],
  );

  const onKeyboardKeyPress = useCallback(
    (button: string) => {
      if (button === "{bksp}" && typeof focusedInput !== "undefined") {
        setPinCode(() => ({ [focusedInput]: "" }));

        const nextFocusedInput = Math.max(1, +focusedInput - 1);

        setFocusedInput(`${nextFocusedInput}`);
      }
    },
    [focusedInput],
  );

  const onPasteCapture = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      const content = event.clipboardData.getData("Text");

      const isValidPin = /^[0-9]{1,6}$/g.test(content);

      if (!isValidPin) {
        return;
      }

      event.preventDefault();

      const pinCode = reduce(
        times(6),
        (acc, key) => ({
          ...acc,
          [key + 1]: content[key],
        }),
        {},
      );

      setPinCode(pinCode);
    },
    [],
  );

  const onFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement, Element>) => {
      const {
        target: { name },
      } = event;
      // trackEvent({ type: "input_focus", value: `pin_${action}` });
      setFocusedInput(`${+name}`);

      if (keyboard.current) {
        keyboard.current.setInput("", name);
      }
    },
    [],
  );

  const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { name, value },
    } = event;

    /**
     * Blur when setting last input is set form keyboard
     */
    if (name === "6" && value && !!value.length) {
      event.target.blur();
    }

    const regex = /^\d$/;

    if (value === "" || regex.test(value)) {
      setPinCode({ [name]: value });

      let nextFocusedInput: number | undefined = +name;

      if (name === "6") {
        nextFocusedInput = undefined;
      } else if (value.length) {
        nextFocusedInput += 1;
      } else {
        nextFocusedInput -= 1;
      }

      setFocusedInput(nextFocusedInput ? `${nextFocusedInput}` : undefined);
    }
  }, []);

  const onBeforeInput = useCallback((event: InputEvent) => {
    switch (event.inputType) {
      case "deleteByComposition":
      case "deleteByCut":
      case "deleteByDrag":
      case "deleteEntireSoftLine":
      case "deleteSoftLineBackward":
      case "deleteSoftLineForward":
      case "deleteHardLineBackward":
      case "deleteHardLineForward":
      case "deleteWordBackward":
      case "deleteWordForward":
      case "deleteContent":
      case "deleteContentForward":
      case "deleteContentBackward": {
        event.preventDefault();

        // eslint-disable-next-line prefer-destructuring
        const name = (event?.currentTarget as HTMLInputElement).name;

        setPinCode({ [name]: "" });

        const nextFocusedInput = Math.max(1, +name - 1);

        setFocusedInput(`${nextFocusedInput}`);

        break;
      }

      default: {
        break;
      }
    }
  }, []);

  useEffect(() => {
    if (!isInWebView) {
      return;
    }

    const ids = Object.keys(defaultPinCode);

    ids.forEach((id) => {
      const element = document.querySelector<HTMLInputElement>(`[id='${id}']`);

      element?.addEventListener("beforeinput", onBeforeInput);
    });

    // eslint-disable-next-line consistent-return
    return () => {
      ids.forEach((id) => {
        const element = document.querySelector<HTMLInputElement>(
          `[id='${id}']`,
        );

        element?.removeEventListener("beforeinput", onBeforeInput);
      });
    };
  }, []);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const {
        code,
        currentTarget: { name },
      } = event;

      switch (code) {
        case "Backspace": {
          event.preventDefault();

          setPinCode({ [name]: "" });

          const nextFocusedInput = Math.max(1, +name - 1);

          setFocusedInput(`${nextFocusedInput}`);

          break;
        }

        case "ArrowRight": {
          event.preventDefault();

          const nextFocusedInput = Math.min(+name + 1, 6);

          setFocusedInput(`${nextFocusedInput}`);

          break;
        }

        case "ArrowLeft": {
          event.preventDefault();
          const nextFocusedInput = Math.max(1, +name - 1);

          setFocusedInput(`${nextFocusedInput}`);

          break;
        }

        case "ArrowUp":
        case "ArrowDown": {
          event.preventDefault();

          break;
        }

        default: {
          break;
        }
      }
    },
    [],
  );

  useClickAway(keyboardBox, (event) => {
    if (event.target && (event.target as HTMLElement)?.tagName === "INPUT") {
      return;
    }

    setFocusedInput(undefined);
  });

  /**
   * Auto-focus input
   */
  useEffect(() => {
    if (focusedInput) {
      const input = document.querySelector<HTMLInputElement>(
        `input[name="${focusedInput}"]`,
      );

      if (input && typeof input.focus === "function") {
        input.focus();
        input.select();
      }
    }
  }, [focusedInput]);

  /**
   * Re-focus first input after we go back to this page.
   * Useful when we get back from code scan with an error
   */
  useEffect(() => {
    if (!code && !focusedInput) {
      setFocusedInput("1");
    }
  }, [code]);

  /**
   * Document-level fallback for HID barcode scanners (keyboard emulators).
   * If a digit keystroke arrives while no input/textarea is focused, redirect
   * it to the first empty PIN input so the scan isn't dropped.
   */
  useEffect(() => {
    if (code) {
      return;
    }

    const onDocKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.altKey || event.metaKey) {
        return;
      }

      if (event.key.length !== 1 || !/^\d$/.test(event.key)) {
        return;
      }

      const active = document.activeElement;

      const activeIsPinInput =
        active instanceof HTMLInputElement && /^[1-6]$/.test(active.name);

      // Don't interfere if focus is already inside a non-pin input (textarea,
      // manual entry, etc.) — that's user typing intentionally.
      if (
        (active instanceof HTMLInputElement ||
          active instanceof HTMLTextAreaElement) &&
        !activeIsPinInput
      ) {
        return;
      }

      // If focus is already in an empty pin input, let the browser handle it
      // normally — that's the happy path for a slow scan or manual entry.
      if (activeIsPinInput && (active as HTMLInputElement).value.length === 0) {
        return;
      }

      const inputs = ([1, 2, 3, 4, 5, 6] as const)
        .map((i) =>
          document.querySelector<HTMLInputElement>(`input[name="${i}"]`),
        )
        .filter((el): el is HTMLInputElement => !!el);

      if (inputs.length === 0) {
        return;
      }

      const target = inputs.find((input) => !input.value);

      if (!target) {
        event.preventDefault();

        return;
      }

      event.preventDefault();
      target.focus();

      const nativeSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        "value",
      )?.set;

      nativeSetter?.call(target, event.key);
      target.dispatchEvent(new Event("input", { bubbles: true }));
    };

    document.addEventListener("keydown", onDocKeyDown);

    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener("keydown", onDocKeyDown);
    };
  }, [code]);

  useEffect(() => {
    const pinCodeArr = Object.values(pinCode);

    const timeout = setTimeout(() => {
      if (pinCodeArr.every((input) => input !== "")) {
        navigate({
          pathname: `${pinCodeArr.join("")}`,
        });

        // setPinCode(defaultPinCode);
        setFocusedInput(undefined);

        if (document.activeElement) {
          (document.activeElement as HTMLInputElement)?.blur();
        }
      }
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [pinCode]);

  const backLink = useMemo(() => {
    if (action === "checkin" && integratedManualCheckin?.enabled) {
      return "/access/option/checkin";
    }

    if (action === "checkout" && checkout?.enabled && manualCheckout?.enabled) {
      return "/access/option/checkout";
    }

    return "/";
  }, []);

  return (
    <Curve>
      <div className="d-flex align-items-center flex-column">
        <BackButton to={backLink} />
        <Title disableAnimation={!mainRoute}>
          <Typography variant="h1">
            <Trans
              i18nKey={(() => {
                if (isHidScannerActive) {
                  return "check:hidScanner.title" as const;
                }
                if (isSocketScannerActive) {
                  return "check:title" as const;
                }
                if (isCameraFeatureEnabled) {
                  return `check:qrScanner.${action}.title` as const;
                }
                return "check:pinOnly.title" as const;
              })()}
            />
          </Typography>
        </Title>
        <div className="check-options">
          {isCameraFeatureEnabled && !isExternalScannerActive && (
            <>
              <Bump disabled={!mainRoute}>
                <motion.div>
                  <ScannerButton
                    variant="contained"
                    disabled={!online}
                    onClick={() => {
                      // trackEvent({ type: "scan_button_click", value: action });
                      navigate(`/scanner/${action}`);
                    }}
                  >
                    {t("check:qrScanner.btn")}
                  </ScannerButton>
                </motion.div>
              </Bump>

              <div className="divider d-flex align-items-center mx-auto">
                <Divider className="col dropdown-divider" />
                <Typography variant="h4" className="col-auto px-5">
                  {t("check:orText")}
                </Typography>
                <Divider className="col dropdown-divider" />
              </div>
            </>
          )}
          <form className="pin__wrapper" autoComplete="off">
            <Reveal className="mx-auto" disabled={!mainRoute}>
              <Typography
                variant="h4"
                className="font-weight-regular pin__title w-100"
              >
                {t("check:pinCode")}
              </Typography>
            </Reveal>
            {([1, 2, 3, 4, 5, 6] as const).map((i) => (
              <MoveUp key={i} disabled={!mainRoute} delay={0.15 + 0.05 * i}>
                <PinInput
                  id={`${i}`}
                  name={`${i}`}
                  min={0}
                  max={9}
                  type={isVirtualKeyboardEnabled ? "text" : "number"}
                  className="pin__input"
                  autoComplete="chrome-off"
                  onFocus={onFocus}
                  onChange={onChange}
                  onKeyDown={onKeyDown}
                  onPasteCapture={onPasteCapture}
                  value={pinCode[i]}
                  maxLength={1}
                  pattern="[0-9]*"
                />
              </MoveUp>
            ))}
          </form>
        </div>
        {isVirtualKeyboardEnabled && (
          <div
            ref={keyboardBox}
            className={clsx("keyboard ", { keyboard__out: !focusedInput })}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <VirtualKeyboard
              ref={keyboard}
              disableButtonHold
              layoutName="pin"
              inputName={focusedInput}
              onChange={onKeyboardChange}
              onKeyPress={onKeyboardKeyPress}
            />
          </div>
        )}
      </div>
      <AggrementDialog
        aggrements={pendingAgreements}
        onDismiss={() => {
          navigate("/");
        }}
        onAccept={accept}
      />
      <Outlet />
      <OfflineModal />
    </Curve>
  );
};

export default CheckPage;
